import React, { ReactNode, useState } from 'react';
import { Card, Button, Space } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';

interface EditableCardProps {
  title: string | ReactNode;
  children: ReactNode;
  onSave?: () => void;
  onCancel?: () => void;
  onEdit?: () => void | null;
  indicatorColor?: string;
  isEditing?: boolean;
  bordered?: boolean; // 是否显示边框
  loading?: boolean; // 是否显示loading
  updating?: boolean; // 非编辑态下，编辑按钮变为旋转 loading（如「更新运单数据中」），禁止进入编辑
  updatingText?: ReactNode; // updating 为真时按钮展示的文案
  editHint?: ReactNode; // 编辑态下，确认按钮旁边的提示文案（如「完成编辑后需确认生效」）
}

const EditableCard: React.FC<EditableCardProps> = ({
  title,
  children,
  onSave,
  onCancel,
  onEdit,
  indicatorColor = 'bg-purple-600',
  isEditing: externalIsEditing,
  bordered = false,
  loading = false,
  updating = false,
  updatingText,
  editHint,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  
  // 如果外部提供了isEditing状态，则使用外部状态；否则使用内部状态
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const handleSave = () => {
    if (onSave) {
      onSave();
    } 
    setInternalIsEditing(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } 
    setInternalIsEditing(false);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } 
    setInternalIsEditing(true);
  };

  return (
    <Card
      className="w-full"
      title={
        <div className="flex items-center">
          <span className={`w-1 h-6 ${indicatorColor} mr-2`}></span>
          <span>{title}</span>
        </div>
      }
      variant={bordered ? 'outlined' : 'borderless'}
      extra={
        <Space>
          {isEditing ? (
            <>
              {editHint && <span className="text-xs text-red-500 mr-1">{editHint}</span>}
              <Button type="text" icon={<CheckOutlined />} onClick={handleSave} />
              <Button type="text" icon={<CloseOutlined />} onClick={handleCancel} />
            </>
          ) : updating ? (
            <Button type="text" size="small" loading disabled>
              {updatingText}
            </Button>
          ) : (
            onEdit && <Button type="text" icon={<EditOutlined />} onClick={handleEdit} />
          )}
        </Space>
      }
      loading={loading}
    >
      {children}
    </Card>
  );
};

export default EditableCard;
